import React, { useEffect, useMemo, useState } from 'react';
import type { SurveyKey } from '../types/database';
import type { SurveyQuestionDefinition, SurveyTokenContext } from '../lib/api/surveys';
import { getSurveyByToken, submitSurveyByToken } from '../lib/api/surveys';
import { captureError } from '../lib/capture-error';

interface SurveyTokenFormProps {
  token: string;
  expectedSurveyKey: SurveyKey;
  surveyTitle: string;
  questions: SurveyQuestionDefinition[];
}

const SurveyTokenForm: React.FC<SurveyTokenFormProps> = ({
  token,
  expectedSurveyKey,
  surveyTitle,
  questions,
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [context, setContext] = useState<SurveyTokenContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState<Record<string, boolean | null>>({});

  useEffect(() => {
    const initialAnswers: Record<string, boolean | null> = {};
    for (const q of questions) {
      initialAnswers[q.key] = null;
    }
    setAnswers(initialAnswers);
  }, [questions]);

  useEffect(() => {
    async function loadSurvey() {
      try {
        setLoading(true);
        const data = await getSurveyByToken(token);
        if (data.survey_key !== expectedSurveyKey) {
          setError('This survey link is for a different survey type.');
          return;
        }
        setContext(data);
      } catch (err) {
        captureError(err, 'Failed to load survey by token');
        setError('Unable to load this survey link.');
      } finally {
        setLoading(false);
      }
    }

    if (token) {
      loadSurvey();
    } else {
      setError('Missing survey token.');
      setLoading(false);
    }
  }, [token, expectedSurveyKey]);

  const allAnswered = useMemo(() => {
    return questions.every((q) => answers[q.key] !== null);
  }, [answers, questions]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allAnswered || !context) {
      setError('Please answer all required questions.');
      return;
    }

    const payload: Record<string, boolean> = {};
    for (const q of questions) {
      payload[q.key] = Boolean(answers[q.key]);
    }

    try {
      setSubmitting(true);
      setError(null);
      await submitSurveyByToken(token, payload, `${expectedSurveyKey}_survey_page`);
      setSubmitted(true);
    } catch (err) {
      captureError(err, 'Failed to submit survey');
      setError(err instanceof Error ? err.message : 'Failed to submit survey.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5" style={{ maxWidth: 760 }}>
      <div className="card shadow-sm">
        <div className="card-body p-4">
          <h1 className="h3 mb-2">{surveyTitle}</h1>
          {context?.event_title && (
            <p className="text-muted mb-4">
              Event: {context.event_title}
            </p>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {(submitted || context?.submitted_at) ? (
            <div className="alert alert-success mb-0" role="alert">
              This survey has already been submitted. Thank you for your response.
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {questions.map((question, idx) => (
                <div className="mb-4" key={question.key}>
                  <p className="mb-2">
                    <strong>{idx + 1}. {question.label}</strong>
                  </p>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name={question.key}
                      id={`${question.key}-yes`}
                      checked={answers[question.key] === true}
                      onChange={() => setAnswers((prev) => ({ ...prev, [question.key]: true }))}
                    />
                    <label className="form-check-label" htmlFor={`${question.key}-yes`}>Yes</label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name={question.key}
                      id={`${question.key}-no`}
                      checked={answers[question.key] === false}
                      onChange={() => setAnswers((prev) => ({ ...prev, [question.key]: false }))}
                    />
                    <label className="form-check-label" htmlFor={`${question.key}-no`}>No</label>
                  </div>
                </div>
              ))}

              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {!submitting && <i className="fas fa-paper-plane me-2" aria-hidden></i>}
                {submitting ? 'Submitting...' : 'Submit Survey'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyTokenForm;
